import type React from "react";
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import toast from "react-hot-toast";

import moment from "moment";
import {
  Button,
  type ButtonProps,
  Icon,
  Loader,
  Popup,
} from "semantic-ui-react";
import { styled, useTheme } from "styled-components";

import type { OpenAPI } from "simplydo/interfaces";

import api from "api";
import { useAppSelector } from "store";

export const eventTypes = {
  createComment: { icon: "paper plane", color: "blue" },
  changedLane: { icon: "arrow right", color: "blue" },
  assignedUser: { icon: "user plus", color: "green" },
  assignedExternalUser: { icon: "user plus", color: "green" },
  unassignedUser: { icon: "user times", color: "red" },
  changedDeadline: { icon: "calendar", color: "blue" },
  changedReviewDate: { icon: "calendar", color: "blue" },
  ideaAssessorAssigned: { icon: "percent", color: "blue" },
  assessmentSubmitted: { icon: "percent", color: "blue" },
  assessmentHasConflict: { icon: "percent", color: "blue" },
};

const EventContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 2.5px;
  width: 430px;
  max-height: 600px;
  min-height: 100px;
  overflow: auto;
`;

const EventItem = styled.div<{ $interacted?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  border-radius: 5px;
  padding: 2.5px;
  cursor: pointer;
  &:hover {
    background-color: #e9ebee;
  }
  .icon-container {
    .i {
      font-size: 0.9em;
      line-height: 1.1em;
    }
    margin-right: 10px;
    margin-left: 5px;
  }
  .info-container {
    > span {
      display: block;
      ${({ $interacted }) =>
        $interacted &&
        `
        color: #B5B6B7;
      `}
    }
    .meta {
      color: "gray";
    }
  }
  margin-bottom: 5px;
  margin-top: 5px;
`;

export type IProjectIdeaEvent = {
  _id: string;
  createdAt: string;
  idea: string;
  user: string;
  forId: string;
  action:
    | "addedToBoard"
    | "assignedUser"
    | "unassignedUser"
    | "changedLane"
    | "changedDeadline"
    | "removedFromBoard"
    | "changedReviewDate"
    | "createComment";
  targetLane?: string;
  userObject?: OpenAPI.Schemas["User"];
  targetUserObject?: OpenAPI.Schemas["User"];
  targetLaneObject?: OpenAPI.Schemas["ProjectLane"];
  previousLaneObject?: OpenAPI.Schemas["ProjectLane"];
  targetIdea?: OpenAPI.Schemas["Idea"];
  deadline?: string;
  interacted?: string[];
  reviewDate?: string;
  comment?: string;
  email?: string;
};

const BoardEvents = ({
  forId,
  setCurrentIdeaId,
  buttonStyle,
  buttonProps = {},
}: {
  forId: string;
  setCurrentIdeaId: Dispatch<SetStateAction<string>>;
  buttonStyle?: React.CSSProperties;
  buttonProps?: ButtonProps;
}) => {
  const user = useAppSelector((state) => state.user);
  const [events, setEvents] = useState<IProjectIdeaEvent[]>([]);
  const [seenEvents, setSeenEvents] = useState<boolean>(true);
  const [canExtend, setCanExtend] = useState<boolean>(false);
  const [eventsState, setEventsState] = useState<{
    page: number;
    limit: number;
  }>({ page: 1, limit: 10 });
  const [loading, setLoading] = useState<boolean>(false);
  const [popupOpen, setPopupOpen] = useState<boolean>(false);
  const [challenge, setChallenge] = useState<
    OpenAPI.Schemas["Challenge"] | null
  >(null);
  const bottomOfEventsRef = useRef<HTMLDivElement | null>(null);
  const { page, limit } = eventsState;
  const userId = user?._id;
  const theme = useTheme();

  const getEvents = useCallback(() => {
    setLoading(page === 1);
    api.boards.getBoardEvents(
      forId,
      { page, limit },
      (data) => {
        if (page === 1) {
          setEvents(data.events);
        } else {
          setEvents((prevEvents) => [...prevEvents, ...data.events]);
          bottomOfEventsRef.current.scrollIntoView({
            behavior: "smooth",
            block: "end",
            inline: "nearest",
          });
        }
        setSeenEvents(data.seen);
        setCanExtend(data.canExtend);
        setLoading(false);
      },
      (err) => {
        setLoading(false);
        toast.error(err.message);
      },
    );
  }, [forId, page, limit]);

  useEffect(() => getEvents(), [getEvents]);

  const getChallenge = useCallback(() => {
    if (!forId) return;
    api.challenges.get(
      forId,
      (data) => {
        if (data) setChallenge(data);
      },
      () => {},
    );
  }, [forId]);

  useEffect(() => getChallenge(), [getChallenge]);

  const onEventClick = useCallback(
    (event) => {
      setPopupOpen(false);
      setCurrentIdeaId(event.idea);
      api.boards.markBoardEventAsInteracted(
        forId,
        event._id,
        () => {
          setEvents((prevEvents) =>
            prevEvents.map((e) => {
              if (e._id === event._id) {
                return { ...e, interacted: [...(e.interacted || []), userId] };
              }
              return e;
            }),
          );
        },
        () => {},
      );
    },
    [forId, setCurrentIdeaId, userId],
  );

  const onOpen = useCallback(() => {
    setPopupOpen(true);
    api.boards.markBoardEventsAsSeen(
      forId,
      () => setSeenEvents(true),
      () => {},
    );
  }, [forId]);

  const getLaneName = useCallback(
    (event, laneKey = "previousLane") => {
      // If previousLane is empty string/null, we know it was moved from default lane
      if (!event[laneKey]) {
        return challenge?.projectBoard?.defaultLane?.name ?? "Default";
      }
      if (event[`${laneKey}Object`]) {
        return event[`${laneKey}Object`].name;
      }
      return "an unknown lane";
      // Else if we know the previous lane object, we can return that
    },
    [challenge],
  );

  if (!events.length) return null;
  return (
    <Popup
      onClose={() => setPopupOpen(false)}
      open={popupOpen}
      on="click"
      wide
      trigger={
        <Button
          color={!seenEvents ? "red" : undefined}
          icon={seenEvents ? "list" : "bell"}
          style={{ ...(buttonStyle ?? {}) }}
          size="small"
          content={theme.sizes.isLargeComputer ? "Recent activity" : null}
          {...buttonProps}
        />
      }
      onOpen={onOpen}
      position="bottom right"
      content={
        <EventContainer>
          {loading ?
            <Loader active style={{ margin: 10 }} />
          : <>
              {events.map((event) => {
                if (!eventTypes[event.action]) return null;
                return (
                  <EventItem
                    key={event._id}
                    onClick={() => onEventClick(event)}
                  >
                    <div className="icon-container">
                      <Icon name={eventTypes[event.action].icon} />
                    </div>
                    <div className="info-container">
                      <span>
                        {event.userObject ?
                          `${event.userObject.profile.fullName}`
                        : "Unknown user"}
                        {event.action === "assignedExternalUser" && (
                          <>
                            {" invited "}
                            <b>
                              {event.email ?
                                `${event.email}`
                              : "unknown user email"}
                            </b>
                            {" to "}
                            <b>{event.targetIdea.name}</b>
                          </>
                        )}
                        {event.action === "assignedUser" && (
                          <>
                            {" assigned "}
                            <b>
                              {event.targetUserObject ?
                                `${event.targetUserObject.profile.fullName}`
                              : "unknown user"}
                            </b>
                            {" to "}
                            <b>{event.targetIdea.name}</b>
                          </>
                        )}
                        {event.action === "unassignedUser" && (
                          <>
                            {" un-assigned "}
                            <b>
                              {event.targetUserObject ?
                                `${event.targetUserObject.profile.fullName}`
                              : "unknown user"}
                            </b>
                            {" from "}
                            <b>{event.targetIdea.name}</b>
                          </>
                        )}
                        {event.action === "changedLane" && (
                          <>
                            {" moved "}
                            <b>{event.targetIdea.name}</b>
                            {" from "}
                            <b>{getLaneName(event)}</b>
                            {" to "}
                            <b>{getLaneName(event, "targetLane")}</b>
                          </>
                        )}
                        {event.action === "changedDeadline" && (
                          <>
                            {" changed the deadline of "}
                            <b>{event.targetIdea.name}</b>
                            {" to "}
                            <b>
                              {event.deadline ?
                                moment(event.deadline).format("DD/MM/YYYY")
                              : "No deadline"}
                            </b>
                          </>
                        )}
                        {event.action === "changedReviewDate" && (
                          <>
                            {" changed the review date of "}
                            <b>{event.targetIdea.name}</b>
                            {" to "}
                            <b>
                              {event.reviewDate ?
                                moment(event.reviewDate).format("DD/MM/YYYY")
                              : "No review date"}
                            </b>
                          </>
                        )}
                        {event.action === "createComment" && (
                          <>
                            {" added a project note to "}
                            <b>{event.targetIdea.name}</b>
                            {". Note: "}
                            <b>
                              "
                              {event?.comment?.length > 50 ?
                                `${event?.comment.substring(0, 50)}...`
                              : event?.comment}
                              "
                            </b>
                          </>
                        )}
                        {event.action === "assessmentSubmitted" && (
                          <>
                            {" submitted an assessment for "}
                            <b>{event.targetIdea.name}</b>
                            {"."}
                          </>
                        )}
                        {event.action === "ideaAssessorAssigned" && (
                          <>
                            {" assigned an assessor to "}
                            <b>{event.targetIdea.name}</b>
                            {"."}
                          </>
                        )}
                        {event.action === "assessmentHasConflict" && (
                          <>
                            {" submitted a conflicting assessment for "}
                            <b>{event.targetIdea.name}</b>
                            {"."}
                          </>
                        )}
                      </span>
                      <span className="meta" style={{ color: "grey" }}>
                        {moment(event.createdAt).format("DD MMM YYYY, HH:mm")}
                      </span>
                    </div>
                  </EventItem>
                );
              })}
              {canExtend ?
                <Button
                  size="small"
                  onClick={() =>
                    setEventsState((prevState) => ({
                      ...prevState,
                      page: prevState.page + 1,
                    }))
                  }
                  content="Load more"
                  style={{
                    marginTop: 5,
                    width: "99%",
                    minHeight: 30,
                    padding: 0,
                  }}
                />
              : null}
              <div ref={bottomOfEventsRef} />
            </>
          }
        </EventContainer>
      }
    />
  );
};

export default BoardEvents;
